import { gql } from '@apollo/client';

import { DISCUSSION } from './discussion';
import { METRIC, UNIT } from './idea-metrics';
import { FILE } from './upload';
import { PERMISSION, ROLES, USER_BASICS, USER_PERMISSION } from './user';

export const A3_BASICS = gql`
  fragment a3Basics on A3 {
    id
    title
    status
    idea {
      id
      name
      problem
    }
    startDate
    endDate
    createdBy {
      ...userBasics
    }
    createdAt
    publishedAt
    tags {
      ...a3Tag
    }
    modelEnabled
    impactedLocations {
      ...a3ImpactedLocation
    }
    processUniverseLevels {
      ...a3ProcessUniverseLevel
    }
    topics {
      ...a3Topic
    }
    rating {
      ...a3Rating
    }
    ratingQuestions {
      ...a3RatingQuestions
    }
    aiAssisted
    collaborators {
      user {
        ...userBasics
      }
      permission {
        resource {
          name
        }
      }
    }
  }
`;

export const A3_DETAIL = gql`
  fragment a3 on A3 {
    id
    title
    problemStatement
    backgroundDescription
    currentDesignFailures
    currentDesignRoot
    currentDesign
    targetDesign
    targetDesignActions
    targetDesignGuidance
    buildExecutePlan
    buildLessonsLearnt
    buildNextSteps
    buildResults
    status
    jsonConfig
    pmLinks
    preview {
      ...file
    }
    idea {
      id
      name
      problem
    }
    startDate
    endDate
    createdBy {
      ...userBasics
    }
    createdAt
    updatedAt
    publishedAt
    attachments {
      id
      attachment {
        ...file
      }
    }
    tags {
      ...a3Tag
    }
    modelEnabled
    impactedLocations {
      ...a3ImpactedLocation
    }
    discussion {
      ...discussion
    }
    processUniverseLevels {
      ...a3ProcessUniverseLevel
    }
    topics {
      ...a3Topic
    }
    metrics {
      ...a3Metric
    }
    collaborators {
      ...userPermission
    }
    rating {
      ...a3Rating
    }
    ratingQuestions {
      ...a3RatingQuestions
    }
    aiAssisted
  }
`;

export const A3_PROCESS_UNIVERSE_LEVEL = gql`
  fragment a3ProcessUniverseLevel on A3ProcessUniverseLevel {
    id
    a3Id
    processUniverseLevel {
      ...processUniverseLevel
    }
    createdBy {
      ...userBasics
    }
    createdAt
  }
`;

export const PROCESS_UNIVERSE_LEVEL = gql`
  fragment processUniverseLevel on ProcessUniverseLevel {
    id
    name
    grandParent
    parentName
    subParent
    createdAt
    updatedAt
  }
`;

export const A3_TAG = gql`
  fragment a3Tag on A3Tag {
    id
    a3Id
    tag {
      id
      name
    }
    createdBy {
      ...userBasics
    }
    createdAt
  }
`;
export const A3_RATING = gql`
  fragment a3Rating on A3RatingResponse {
    id
    a3Id
    questionId
    answer
    createdBy {
      ...userBasics
    }
  }
`;

export const A3_RATING_QUESTION = gql`
  fragment a3RatingQuestions on A3RatingQuestions {
    id
    questionText
    weight
    orderNumber
  }
`;

export const IMPACTED_LOCATION = gql`
  fragment impactedLocation on ImpactedLocation {
    id
    name
    createdAt
    updatedAt
  }
`;

export const A3_IMPACTED_LOCATION = gql`
  fragment a3ImpactedLocation on A3ImpactedLocation {
    id
    a3Id
    impactedLocation {
      ...impactedLocation
    }
    createdBy {
      ...userBasics
    }
    createdAt
  }
`;

export const A3_TOPIC = gql`
  fragment a3Topic on A3Topic {
    id
    a3Id
    topic {
      id
      name
    }
    createdBy {
      ...userBasics
    }
    createdAt
  }
`;

export const A3_METRIC = gql`
  fragment a3Metric on A3Metric {
    id
    a3Id
    metric {
      ...metric
    }
    before
    after
    metricType
    createdAt
  }
`;

export const A3_CONNECTION = gql`
  fragment a3Connection on A3Connection {
    edges {
      cursor
      node {
        ...a3Basics
      }
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
`;

export const A3_LIST_DTO = gql`
  fragment a3ListDto on A3ListDto {
    items {
      ...a3Basics
    }
    total
  }
`;

export const GET_A3 = gql`
  query A3ById($id: Int!) {
    a3ById(id: $id) {
      ...a3
    }
  }
  ${A3_DETAIL}
  ${USER_BASICS}
  ${A3_TAG}
  ${A3_IMPACTED_LOCATION}
  ${IMPACTED_LOCATION}
  ${DISCUSSION}
  ${A3_TOPIC}
  ${A3_METRIC}
  ${PROCESS_UNIVERSE_LEVEL}
  ${A3_PROCESS_UNIVERSE_LEVEL}
  ${METRIC}
  ${USER_PERMISSION}
  ${ROLES}
  ${PERMISSION}
  ${FILE}
  ${UNIT}
  ${A3_RATING}
  ${A3_RATING_QUESTION}
`;

export const CREATE_A3 = gql`
  mutation CreateA3($input: CreateA3Input!) {
    createA3(input: $input) {
      id
      title
    }
  }
`;

export const PATCH_A3 = gql`
  mutation PatchA3($input: PatchA3Input!) {
    patchA3(input: $input) {
      id
      title
    }
  }
`;

export const PUBLISH_A3 = gql`
  mutation PublishA3($input: PatchA3Input!) {
    publishA3(input: $input) {
      id
      title
    }
  }
`;

export const LIST_IMPACTED_LOCATIONS = gql`
  query ListImpactedLocations {
    listImpactedLocations {
      id
      name
      parentName
    }
  }
`;

export const A3_LIST_TAG = gql`
  query ListTags {
    listTags {
      id
      name
    }
  }
`;

export const A3_CREATE_TAG = gql`
  mutation CreateTag($input: CreateTagInput!) {
    createTag(input: $input) {
      id
      name
    }
  }
`;
export const LIST_A3_FOR_USER = gql`
  query ListA3ForUser($first: Int, $after: String, $filter: A3SearchFilter) {
    listA3ForUser(first: $first, after: $after, filter: $filter) {
      ...a3Connection
    }
  }
  ${A3_BASICS}
  ${USER_BASICS}
  ${A3_TAG}
  ${A3_CONNECTION}
  ${A3_IMPACTED_LOCATION}
  ${IMPACTED_LOCATION}
  ${A3_TOPIC}
  ${PROCESS_UNIVERSE_LEVEL}
  ${A3_PROCESS_UNIVERSE_LEVEL}
  ${FILE}
  ${A3_RATING}
  ${A3_RATING_QUESTION}
`;

export const LIST_A3_FOR_USER_TOTAL = gql`
  query ListA3ForUserTotal(
    $first: Int
    $after: String
    $filter: A3SearchFilter
  ) {
    listA3ForUserTotal(first: $first, after: $after, filter: $filter)
  }
`;

export const LIST_A3_FOR_MY_SPACE = gql`
  query ListA3ForMySpace(
    $page: Int = 1
    $limit: Int = 10
    $filter: A3SearchFilter
  ) {
    listA3ForMySpace(page: $page, limit: $limit, filter: $filter) {
      ...a3ListDto
    }
  }
  ${A3_BASICS}
  ${USER_BASICS}
  ${A3_TAG}
  ${A3_LIST_DTO}
  ${A3_IMPACTED_LOCATION}
  ${IMPACTED_LOCATION}
  ${A3_TOPIC}
  ${PROCESS_UNIVERSE_LEVEL}
  ${A3_PROCESS_UNIVERSE_LEVEL}
  ${FILE}
  ${A3_RATING}
  ${A3_RATING_QUESTION}
`;

export const LIST_A3_FOR_BADGE = gql`
  query ListA3ForBadge(
    $page: Int = 1
    $limit: Int = 10
    $filter: A3SearchFilter
  ) {
    listA3ForMySpace(page: $page, limit: $limit, filter: $filter) {
      items {
        id
        title
        status
        idea {
          id
          name
          problem
        }
        startDate
        endDate
        createdBy {
          ...userBasics
        }
        metrics {
          ...a3Metric
        }
        rating {
          ...a3Rating
        }
        ratingQuestions {
          ...a3RatingQuestions
        }
      }
      total
    }
  }
  ${A3_METRIC}
  ${METRIC}
  ${UNIT}
  ${USER_BASICS}
  ${FILE}
  ${A3_RATING}
  ${A3_RATING_QUESTION}
`;

export const LIST_MY_A3 = gql`
  query MyA3($first: Int, $after: String, $filter: A3SearchFilter) {
    myA3(first: $first, after: $after, filter: $filter) {
      edges {
        cursor
        node {
          ...a3Basics
          collaborators {
            user {
              ...userBasics
            }
            permission {
              resource {
                name
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
  ${A3_BASICS}
  ${USER_BASICS}
  ${A3_TAG}
  ${A3_IMPACTED_LOCATION}
  ${IMPACTED_LOCATION}
  ${A3_TOPIC}
  ${PROCESS_UNIVERSE_LEVEL}
  ${A3_PROCESS_UNIVERSE_LEVEL}
  ${FILE}
  ${A3_RATING}
  ${A3_RATING_QUESTION}
`;

export const LIST_MY_A3_TOTAL = gql`
  query MyA3Total($first: Int, $after: String, $filter: A3SearchFilter) {
    myA3Total(first: $first, after: $after, filter: $filter)
  }
`;

export const DELETE_A3 = gql`
  mutation DeleteA3ById($id: Int!) {
    deleteA3ById(a3Id: $id)
  }
`;

export const RATE_A3 = gql`
  mutation RateA3($input: CreateA3Rating!) {
    rateA3(input: $input) {
      ...a3Rating
    }
  }
  ${USER_BASICS}
  ${A3_RATING}
  ${FILE}
`;

export const GET_A3_STATISTICS_FOR_USER = gql`
  query getA3StatisticsForUser($userId: Int!) {
    getA3StatisticsForUser(userId: $userId) {
      owned
      sponsored
      coached
      member
    }
  }
`;

export const GET_A3_BADGE_PROGRESS = gql`
  query A3BadgeProgress {
    a3BadgeProgress {
      total
      progress
      coached
      practitioner
      foundation
      __typename
    }
  }
`;

export const GET_A3_USER_STATISTICS = gql`
  query GetA3UserStatistics {
    getA3UserStatistics {
      colleaguesEngaged
      a3Submission
      owner
      sponsor
      coach
      trainingCompletion
    }
  }
`;

export const GET_A3_STATISTICS_CHART = gql`
  query GetA3StatisticsChart($startDate: String!, $endDate: String!) {
    getA3StatisticsChart(startDate: $startDate, endDate: $endDate) {
      months
      colleaguesEngaged
      a3Submission
      owner
      sponsor
      coach
    }
  }
`;

export const GET_A3_BY_DAYS = gql`
  query countA3ByDays($filter: A3DashboardFilter) {
    countA3ByDays(filter: $filter) {
      group
      count
    }
  }
`;

export const GET_A3_BY_PROCESS_UNIVERSE_LEVEL = gql`
  query CountA3ByProcessUniverseLevel($filter: A3DashboardFilter) {
    countA3ByProcessUniverseLevel(filter: $filter) {
      group
      count
    }
  }
`;

export const GET_A3_BY_IMPACTED_LOCATION = gql`
  query CountA3ByImpactedLocations($filter: A3DashboardFilter) {
    countA3ByImpactedLocations(filter: $filter) {
      group
      count
    }
  }
`;

export const GET_A3_WITH_METRICS = gql`
  query CountA3WithMetrics($filter: A3DashboardFilter) {
    countA3WithMetrics(filter: $filter)
  }
`;

export const GET_A3_METRICS_DETAILS = gql`
  query GetA3MetricsDetails($filter: A3DashboardFilter) {
    getA3MetricsDetails(filter: $filter) {
      metric
      before
      after
      a3
      unit
    }
  }
`;

export const GET_COUNT_A3_USERS_BY_BADGES = gql`
  query CountA3UsersByBadges($filter: TimeRangeFilterDto) {
    countA3UsersByBadges(filter: $filter) {
      group
      count
    }
  }
`;

export const GET_A3_RATING_STATISTICS = gql`
  query GetA3RatingStatistics($filter: A3DashboardFilter) {
    getA3RatingStatistics(filter: $filter) {
      a3Rated
      averageRating
      a3ByRatings {
        rating
        a3Count
        percentage
      }
    }
  }
`;

export const LIST_PROCESS_UNIVERSE_LEVELS = gql`
  query ListProcessUniverseLevels {
    listProcessUniverseLevels {
      ...processUniverseLevel
    }
  }
  ${PROCESS_UNIVERSE_LEVEL}
`;

export const DOWNLOAD_A3_LIST = gql`
  query DownloadA3($startDate: String!, $endDate: String!) {
    downloadA3(startDate: $startDate, endDate: $endDate)
  }
`;

export const DOWNLOAD_A3_USER_REPORT = gql`
  query DownloadA3UserReport {
    downloadA3UserReport
  }
`;

export const GET_MD_STATISTICS = gql`
  query GetA3MdStatistics($filter: A3MdDashboardFilter) {
    getA3MdStatistics(filter: $filter) {
      submissions
      averageAging
      averageRating
      mdEngaged
    }
  }
`;

export const GET_MD_LEADERBOARD = gql`
  query GetMdLeaderboard(
    $userId: [Int]
    $page: Int
    $limit: Int
    $sort: [MDLeaderboardSortOptions]
    $sortDirection: Boolean
  ) {
    getMdLeaderboard(
      userId: $userId
      page: $page
      limit: $limit
      sort: $sort
      sortDirection: $sortDirection
    ) {
      items {
        user {
          ...userBasics
          psid
        }
        a3Count
        sponsoredCount
        avgAge
        avgRating
        metrics {
          metric
          afterValue
        }
      }
      total
    }
  }
  ${USER_BASICS}
  ${FILE}
`;

export const GET_MD_PERFORMANCE = gql`
  query GetA3MDStatisticsChart($filter: A3MdDashboardFilter) {
    getA3MDStatisticsChart(filter: $filter) {
      months
      colleaguesEngaged
      a3Submission
      owner
      sponsor
      coach
    }
  }
`;

export const GET_MD_PU_LEVEL = gql`
  query CountA3MdByPUL($filter: A3MdDashboardFilter) {
    countA3MdByPUL(filter: $filter) {
      group
      count
    }
  }
`;

export const GET_A3_METRIC_DETAIL = gql`
  query GetA3MdMetricsDetails($filter: A3MdDashboardFilter) {
    getA3MdMetricsDetails(filter: $filter) {
      id
      metric
      before
      after
      a3
      unit
    }
  }
`;

export const ANALYZE_A3_PUBLISHER = gql`
  subscription AnalyzeA3Publisher(
    $problemStatement: String!
    $backgroundDescription: String
  ) {
    analyzeA3Publisher(
      problemStatement: $problemStatement
      backgroundDescription: $backgroundDescription
    ) {
      id
      responseLog
      result {
        criteriaSlug
        criteriaMet
        reasoning
      }
      statusCode
    }
  }
`;

export const REVISE_A3_PUBLISHER = gql`
  subscription ReviseA3Publisher(
    $problemStatement: String!
    $backgroundDescription: String
    $responseLog: String!
  ) {
    reviseA3Publisher(
      problemStatement: $problemStatement
      backgroundDescription: $backgroundDescription
      responseLog: $responseLog
    ) {
      id
      items {
        name
        value
      }
      statusCode
    }
  }
`;

export const GET_A3_ANALYZE_CRITERIAS = gql`
  query ListA3AnalyzeCriterias {
    listA3AnalyzeCriterias {
      id
      slug
      criteria
      orderNum
    }
  }
`;
